import { createGlobalStyle } from 'styled-components';

// 使用<link>标签在HTML中引入字体，而不是在CSS中使用@import
// 这个注释是提醒开发者在index.html中添加以下代码：
// <link href="https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&display=swap" rel="stylesheet">

const GlobalStyles = createGlobalStyle`
  @font-face {
    font-family: 'HanaMinA';
    src: url('https://cdn.jsdelivr.net/gh/max32002/max-hana@1.1/webfont/HanaMinA.woff2') format('woff2'),
         url('https://cdn.jsdelivr.net/gh/max32002/max-hana@1.1/webfont/HanaMinA.woff') format('woff');
    font-weight: normal;
    font-style: normal;
    font-display: swap;
  }

  @font-face {
    font-family: 'Hanson';
    src: url('/fonts/Hanson-Bold.ttf') format('truetype');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }

  :root {
    font-size: 16px;
    
    @media (max-width: 1440px) {
      font-size: 15px;
    }
    
    @media (max-width: 1024px) {
      font-size: 14px;
    }
    
    @media (max-width: 768px) {
      font-size: 13px;
    }
    
    @media (max-width: 480px) {
      font-size: 12px;
    }
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
    font-family: 'HanaMinA', serif;
    -webkit-text-size-adjust: 100%; /* 防止iOS中文字自动调整大小 */
    -webkit-tap-highlight-color: transparent; /* 移除点击元素时的高亮 */
  }

  html, body {
    width: 100%;
    min-height: 100vh;
    overflow-x: hidden;
    font-family: 'HanaMinA', serif;
    touch-action: manipulation; /* 防止双击缩放 */
  }

  body {
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    background-color: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.text};
    transition: all 0.3s ease;
    text-rendering: optimizeLegibility;
    /* 设置主题过渡效果 */
    transition-property: background-color, color;
    transition-duration: 0.3s;
    /* 解决iOS点击延迟问题 */
    cursor: default;
    /* 限制视口宽度 */
    overflow-wrap: break-word;
    word-wrap: break-word;
  }

  img {
    max-width: 100%;
    height: auto;
    display: block;
    /* 防止图像在iOS上缩放 */
    -webkit-user-select: none;
    user-select: none;
    /* 更好的图像渲染 */
    image-rendering: -webkit-optimize-contrast;
    image-rendering: crisp-edges;
  }

  .responsive-container {
    width: 100%;
    max-width: 100%;
    margin: 0 auto;
    padding: 0 0.3rem;

    @media (max-width: 1440px) {
      max-width: 100%;
    }

    @media (max-width: 1024px) {
      padding: 0 0.25rem;
    }

    @media (max-width: 768px) {
      padding: 0 0.2rem;
    }

    @media (max-width: 480px) {
      padding: 0 0.15rem;
    }
  }

  .apple-button {
    background: ${({ theme }) => theme.buttonBg};
    border: none;
    border-radius: 0.75rem;
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
    font-weight: 600;
    color: ${({ theme }) => theme.text};
    cursor: pointer;
    box-shadow: ${({ theme }) => theme.buttonShadow};
    transition: all 0.3s ease;
    /* 改善移动端点击体验 */
    touch-action: manipulation;

    &:hover {
      transform: translateY(-2px);
      box-shadow: ${({ theme }) => theme.buttonShadow}, 0 10px 20px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: translateY(1px);
    }
  }

  .card {
    background: ${({ theme }) => theme.cardBg};
    border-radius: 1rem;
    padding: 1.5rem;
    box-shadow: ${({ theme }) => theme.cardShadow};
    margin-bottom: 1.5rem;
    
    @media (max-width: 480px) {
      padding: 1rem;
      border-radius: 0.75rem;
    }
  }

  h1, h2, h3, h4, h5, h6, p, span, div {
    font-family: 'HanaMinA', serif;
  }

  h1 {
    font-size: 2.5rem;
    @media (max-width: 768px) {
      font-size: 2rem;
    }
  }

  h2 {
    font-size: 2rem;
    @media (max-width: 768px) {
      font-size: 1.75rem;
    }
  }

  h3 {
    font-size: 1.75rem;
    @media (max-width: 768px) {
      font-size: 1.5rem;
    }
  }

  h4 {
    font-size: 1.5rem;
    @media (max-width: 768px) {
      font-size: 1.25rem;
    }
  }

  h5 {
    font-size: 1.25rem;
    @media (max-width: 768px) {
      font-size: 1.1rem;
    }
  }

  h6 {
    font-size: 1rem;
  }

  p {
    margin-bottom: 1rem;
    font-size: 1rem;
    line-height: 1.6;
  }

  a {
    color: inherit;
    text-decoration: none;
    touch-action: manipulation;
  }

  input, textarea, select {
    background: ${({ theme }) => theme.cardBg};
    border: 1px solid ${({ theme }) => theme.border};
    border-radius: 0.5rem;
    padding: 0.5rem 0.75rem;
    color: ${({ theme }) => theme.text};
    font-size: 1rem;
    -webkit-appearance: none; /* 移除iOS默认样式 */
    
    @media (max-width: 480px) {
      font-size: 16px; /* 防止iOS缩放 */
    }
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.primary};
    }
  }

  /* 强制设置模态框中输入框文字颜色 */
  .ant-input, 
  .ant-input-affix-wrapper input,
  .ant-input-affix-wrapper .ant-input,
  input.ant-input,
  .ant-input-affix-wrapper > input.ant-input,
  [class*="Modal"] input,
  [class*="modal"] input {
    color: #000000 !important;
    -webkit-text-fill-color: #000000 !important;
    background-color: #F5F5F5 !important;
  }
  
  /* 保持输入框在激活状态下仍为灰色背景 */
  .ant-input:focus,
  .ant-input-focused, 
  .ant-input-affix-wrapper:focus,
  .ant-input-affix-wrapper-focused,
  input:focus,
  .ant-input:hover,
  .ant-input-affix-wrapper:hover,
  input:hover {
    background-color: #F5F5F5 !important;
    border-color: #FF4D4F !important;
    box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2) !important;
  }
  
  /* 处理密码输入框和后缀图标 */
  .ant-input-affix-wrapper .ant-input,
  .ant-input-affix-wrapper .ant-input-suffix {
    color: #000000 !important;
  }
  
  /* 解决自动填充问题 */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus,
  input:-webkit-autofill:active {
    -webkit-box-shadow: 0 0 0 1000px #F5F5F5 inset !important;
    -webkit-text-fill-color: #000000 !important;
    caret-color: #000000 !important;
  }

  button {
    cursor: pointer;
    font-family: 'HanaMinA', serif;
    touch-action: manipulation;
    -webkit-appearance: none;
    -webkit-tap-highlight-color: transparent;
  }

  /* 移动设备特殊调整 */
  @media (hover: none) {
    .apple-button:hover, button:hover {
      transform: none;
      box-shadow: ${({ theme }) => theme.buttonShadow};
    }
    
    a:active, button:active, .apple-button:active {
      opacity: 0.7;
      transition: opacity 0.1s;
    }
  }
  
  /* 暗色模式额外调整 */
  @media (prefers-color-scheme: dark) {
    html:not([data-theme='light']) body {
      background-color: ${({ theme }) => theme.isDarkMode ? theme.background : ''};
      color: ${({ theme }) => theme.isDarkMode ? theme.text : ''};
    }
  }
`;

export default GlobalStyles; 